import { Badge } from '@/components/ui/badge';
import { Zap, Brain, Clock, Hand, CheckCircle, XCircle, Loader2, Pause } from 'lucide-react';

interface ParsingMethodBadgeProps {
  method: string | null;
}

export function ParsingMethodBadge({ method }: ParsingMethodBadgeProps) {
  const getMethodConfig = (m: string | null) => {
    switch (m) {
      case 'vendor_api':
        return { icon: Zap, label: 'Vendor API', variant: 'default' as const, className: 'bg-blue-500 hover:bg-blue-600' };
      case 'ai_parsing':
        return { icon: Brain, label: 'AI Parsing', variant: 'secondary' as const, className: 'bg-purple-500 hover:bg-purple-600' };
      case 'manual':
        return { icon: Hand, label: 'Manual', variant: 'outline' as const, className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500' };
      case 'waiting':
        return { icon: Clock, label: 'Waiting', variant: 'outline' as const, className: 'bg-muted' };
      default:
        return { icon: Clock, label: 'Unknown', variant: 'outline' as const, className: 'bg-muted' };
    }
  };

  const config = getMethodConfig(method);
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={`gap-1 ${config.className}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

interface ParsingStatusBadgeProps {
  status: string | null;
}

export function ParsingStatusBadge({ status }: ParsingStatusBadgeProps) {
  const getStatusConfig = (s: string | null) => {
    switch (s) {
      case 'pending_api':
        return { icon: Clock, label: 'Pending API', variant: 'default' as const, className: 'bg-blue-500/10 text-blue-600 border-blue-500' };
      case 'api_success':
        return { icon: CheckCircle, label: 'API Success', variant: 'default' as const, className: 'bg-green-500 hover:bg-green-600' };
      case 'api_failed':
        return { icon: XCircle, label: 'API Failed', variant: 'destructive' as const };
      case 'waiting_trigger':
        return { icon: Pause, label: 'Awaiting Action', variant: 'outline' as const, className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500' };
      case 'ai_parsing':
        return { icon: Loader2, label: 'AI Parsing...', variant: 'secondary' as const, className: 'bg-purple-500/10 text-purple-600 animate-pulse' };
      case 'ai_success':
        return { icon: CheckCircle, label: 'Parsed', variant: 'default' as const, className: 'bg-green-500 hover:bg-green-600' };
      case 'ai_failed':
        return { icon: XCircle, label: 'Failed', variant: 'destructive' as const };
      default:
        return { icon: Clock, label: 'Unknown', variant: 'outline' as const, className: 'bg-muted' };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={`gap-1 ${config.className}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}
